import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconButton, Badge, Menu, Button } from '@mui/material';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import { notificationService } from '../../Services/NotificationService';
import { enablePush, getPermission, isIos, isStandalone, syncPush } from '../../Utils/pushNotifications';
import './Notifications.css';

const POLL_INTERVAL_MS = 30000;

function timeAgo(dateString) {
  const seconds = Math.floor((new Date() - new Date(dateString)) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const laboratoryId = sessionStorage.getItem('LabID');
  const userId = sessionStorage.getItem('UserID');

  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const pollRef = useRef(null);
  const [pushPermission, setPushPermission] = useState(getPermission());
  const [enablingPush, setEnablingPush] = useState(false);
  // False when the super admin has turned off notifications or push for this
  // lab - then there's no point offering "Enable" (the server won't push).
  const [pushAllowedForLab, setPushAllowedForLab] = useState(false);

  const fetchUnreadCount = async () => {
    if (!laboratoryId || !userId) return;
    try {
      const count = await notificationService.getUnreadCount(laboratoryId, userId);
      setUnreadCount(count);
    } catch (error) {
      console.error('Failed to fetch unread notification count:', error);
    }
  };

  const fetchNotifications = async () => {
    if (!laboratoryId || !userId) return;
    try {
      const response = await notificationService.getForUser(laboratoryId, userId, { pageSize: 15 });
      setNotifications(response.items || []);
      setUnreadCount(response.unreadCount || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  useEffect(() => {
    fetchUnreadCount();
    pollRef.current = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laboratoryId, userId]);

  // Keep this device's push subscription pointed at the logged-in user, and
  // refresh the badge as soon as the service worker receives a push.
  useEffect(() => {
    if (!laboratoryId || !userId) return undefined;
    syncPush(laboratoryId, userId);
    notificationService
      .getSetting(laboratoryId)
      .then((s) => setPushAllowedForLab((s?.isAllowed ?? true) && (s?.isPushAllowed ?? true)))
      .catch((error) => console.error('Failed to load notification setting:', error));

    if (!('serviceWorker' in navigator)) return undefined;
    const onMessage = (e) => {
      if (e.data && e.data.type === 'labworld-push') fetchUnreadCount();
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laboratoryId, userId]);

  const handleEnablePush = async () => {
    setEnablingPush(true);
    try {
      setPushPermission(await enablePush(laboratoryId, userId));
    } catch (error) {
      console.error('Failed to enable push notifications:', error);
    } finally {
      setEnablingPush(false);
    }
  };

  // What to show in the push row of the dropdown (null = nothing, already on).
  const renderPushRow = () => {
    if (!pushAllowedForLab || pushPermission === 'granted') return null;
    if (pushPermission === 'denied') {
      return (
        <div className="notification-bell-push">
          Phone notifications are blocked. Allow them in your browser's site settings.
        </div>
      );
    }
    if (pushPermission === 'unsupported') {
      // iPhones only support push for sites added to the Home Screen.
      if (isIos() && !isStandalone()) {
        return (
          <div className="notification-bell-push">
            To get notifications on this iPhone, tap Share → Add to Home Screen, then open Lab365 from that icon.
          </div>
        );
      }
      return null;
    }
    return (
      <div className="notification-bell-push">
        <span>Get notified on this device even when Lab365 is closed.</span>
        <Button size="small" variant="contained" disabled={enablingPush} onClick={handleEnablePush}>
          {enablingPush ? 'Enabling…' : 'Enable'}
        </Button>
      </div>
    );
  };

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    fetchNotifications();
  };

  const handleClose = () => setAnchorEl(null);

  const handleItemClick = async (notification) => {
    if (!notification.isRead) {
      try {
        await notificationService.markAsRead(notification.id, userId);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }

    // Close the dropdown and jump to the patient this notification is
    // about, when it has one (report printed / order created events do).
    handleClose();
    if (notification.patientId) {
      navigate(`/Home/PatientProfile/${notification.patientId}`);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllAsRead(laboratoryId, userId);
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  if (!laboratoryId || !userId) return null;

  return (
    <>
      <IconButton onClick={handleOpen}>
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsRoundedIcon sx={{ color: '#555' }} />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ className: 'notification-bell-paper' }}
        MenuListProps={{ className: 'notification-bell-menulist' }}
      >
        <div className="notification-bell-menu">
          <div className="notification-bell-header">
            <span>Notifications</span>
            {unreadCount > 0 && (
              <Button className="notification-bell-mark-all" size="small" onClick={handleMarkAllRead}>
                Mark all as read
              </Button>
            )}
          </div>
          {renderPushRow()}
          <div className="notification-bell-list">
            {notifications.length === 0 ? (
              <div className="notification-bell-empty">No notifications yet</div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  className={`notification-bell-item ${n.isRead ? '' : 'unread'}`}
                  onClick={() => handleItemClick(n)}
                  role="button"
                >
                  {!n.isRead && <span className="notification-bell-item-dot" />}
                  <div className="notification-bell-item-body">
                    <div className="notification-bell-item-message">{n.message}</div>
                    <div className="notification-bell-item-meta">{timeAgo(n.createdAt)}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </Menu>
    </>
  );
}
